import { useState } from "react";
import { Loader2 } from "lucide-react";
import { QUESTIONS } from "../../data/applyQuestions";
import { validateApplicationContact } from "../../lib/validation";
import { createApplication } from "../../lib/api";
import { OptionGroup } from "./OptionGroup";

const QUESTIONS_ERROR = "Please answer all four questions so we can point you in the right direction.";

const Field = ({ id, label, error, ...input }) => (
  <div>
    <label htmlFor={`apply-${id}`} className="mb-1.5 block text-[13px] font-semibold text-[#2a2214]">{label}</label>
    <input id={`apply-${id}`} className="field" aria-invalid={!!error} aria-describedby={error ? `apply-${id}-error` : undefined} data-testid={`apply-${id}-input`} {...input} />
    {error && <p id={`apply-${id}-error`} className="mt-1.5 text-[13px] text-[#b3261e]" data-testid={`apply-${id}-error`}>{error}</p>}
  </div>
);

export const ApplyForm = ({ values, setValues, onDone }) => {
  const [errors, setErrors] = useState({});
  const [questionsError, setQuestionsError] = useState("");
  const [apiError, setApiError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (v) => {
    setValues((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((s) => ({ ...s, [k]: undefined }));
  };
  const onInput = (k) => (e) => set(k)(e.target.value);

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateApplicationContact(values);
    const unanswered = QUESTIONS.some((q) => !values[q.key]);
    setErrors(errs);
    setQuestionsError(unanswered ? QUESTIONS_ERROR : "");
    setApiError("");
    if (Object.keys(errs).length || unanswered) return;
    setSubmitting(true);
    try {
      const { data } = await createApplication({ ...values, full_name: values.full_name.trim(), email: values.email.trim(), phone: values.phone.trim() });
      onDone(data.invest);
    } catch {
      setApiError("Something went wrong sending your application. Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="apply-form">
      <p className="font-mono-plex text-[11px] tracking-[0.16em] text-[#8a6416]" data-testid="apply-step-label">Step 1 of 2 · Short application</p>
      <div className="mt-6 space-y-4">
        <Field id="full_name" label="Full name" autoComplete="name" value={values.full_name} onChange={onInput("full_name")} error={errors.full_name} />
        <Field id="phone" label="Mobile" type="tel" inputMode="tel" autoComplete="tel-national" value={values.phone} onChange={onInput("phone")} error={errors.phone} />
        <Field id="email" label="Email" type="email" inputMode="email" autoComplete="email" value={values.email} onChange={onInput("email")} error={errors.email} />
      </div>
      <div className="mt-8 space-y-7">
        {QUESTIONS.map((q) => <OptionGroup key={q.key} question={q} value={values[q.key]} onChange={(v) => { set(q.key)(v); setQuestionsError(""); }} />)}
      </div>
      {questionsError && <p role="alert" className="mt-6 text-[14px] font-medium text-[#b3261e]" data-testid="apply-questions-error">{questionsError}</p>}
      {apiError && <p role="alert" className="mt-6 rounded-lg border border-[#b3261e]/30 bg-[#b3261e]/10 px-3 py-2.5 text-[13px] text-[#8c1d18]" data-testid="apply-api-error">{apiError}</p>}
      <button type="submit" disabled={submitting} className="mt-7 flex w-full items-center justify-center gap-2 rounded-full border border-[#e8b84a]/60 bg-[#0a0a0a] px-6 py-4 text-[15px] font-bold tracking-[0.08em] text-[#f3d27c] transition-[transform,background-color] duration-200 hover:-translate-y-px hover:bg-black disabled:opacity-70" data-testid="apply-continue-button">
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        CONTINUE →
      </button>
      <p className="mt-4 text-center text-[12px] text-[#6b5d40]">Your answers help us prepare for the call. There is no obligation to enroll.</p>
    </form>
  );
};
